import { SerpDisplayType } from '~/analytics/ActionAnalytics/actions/actionTypes';
import { RequestParams } from '~/core/api/RequestTypes';
import { ResponseResult } from '~/core/api/ResponseTypes';
import { ResultType } from '~/core/results/types/results.type';
import SRError from '~/utils/errors/srError';
import { UpdateFilterPayload_ControlsState } from '../features/filtering/types/filters.type';
import { UpdateIndexFilterPayload_ControlsState } from '../features/indexFiltering/types/indices.type';
import { CancelAllFiltersPayload, CancelFilterPayload } from '../state/actions';
import { CancelAllFiltersEvent } from './types/eventTypes/CancelAllFiltersEvent';
import { CancelFilterEvent } from './types/eventTypes/CancelFilterEvent';
import { SERPCloseEvent } from './types/eventTypes/CloseEvent';
import { SERPErrorEvent } from './types/eventTypes/ErrorEvent';
import { FilterEvent } from './types/eventTypes/FilterEvent';
import { GoToResultEvent } from './types/eventTypes/GoToResultEvent';
import { IndicesFilterEvent } from './types/eventTypes/IndicesFilterEvent';
import { OpenEvent } from './types/eventTypes/OpenEvent';
import { ResetQueryEvent } from './types/eventTypes/ResetQueryEvent';
import { SearchEvent } from './types/eventTypes/SearchEvent';
import { SERPConfirmationEventType, SERPEvent, SERPEventType } from './types/SERPEvents';

export type ErrorEventParams = {
    searchQuery: string;
    error: Error | SRError;
};

export type SearchEventParams = {
    searchQuery: string;
    totalResultsCount: number;
    requestParams: RequestParams;
    results: Array<ResponseResult>;
    serpType: SerpDisplayType;
};

export type CloseEventParams = {
    searchQuery: string;
    resultsCount: number;
    loadingFinished: boolean;
    serpType: SerpDisplayType;
};

export type OpenEventParams = {
    serpType: SerpDisplayType;
};

export type GoToResultEventParams = {
    searchQuery: string;
    resultsCount: number;
    resultType: ResultType;
    url: string;
    order: number;
    serpType: SerpDisplayType;
    button: number;
    key: string;
};

export type IndicesFilterEventParams = {
    searchQuery: string;
    actionPayload: UpdateIndexFilterPayload_ControlsState;
};

export type FilterEventParams = {
    searchQuery: string;
    actionPayload: UpdateFilterPayload_ControlsState;
};

export type CancelFilterEventParams = {
    searchQuery: string;
    actionPayload: CancelFilterPayload;
};

export type CancelAllFiltersEventParams = {
    searchQuery: string;
    actionPayload: CancelAllFiltersPayload;
};

export class EventFactory {
    constructor() {
        this.createOpenEvent = this.createOpenEvent.bind(this);
        this.createCloseEvent = this.createCloseEvent.bind(this);
        this.createSearchEvent = this.createSearchEvent.bind(this);
        this.createGoToResultEvent = this.createGoToResultEvent.bind(this);
        this.createIndicesFilterEvent = this.createIndicesFilterEvent.bind(this);
        this.createFilterEvent = this.createFilterEvent.bind(this);
        this.createCancelFilterEvent = this.createCancelFilterEvent.bind(this);
        this.createCancelAllFiltersEvent = this.createCancelAllFiltersEvent.bind(this);
        this.createErrorEvent = this.createErrorEvent.bind(this);
        this.createResetQueryEvent = this.createResetQueryEvent.bind(this);
    }

    public createResetQueryEvent(): ResetQueryEvent {
        return this.createEvent<ResetQueryEvent>(SERPEventType.RESET_QUERY, {});
    }

    public createSearchEvent({
        searchQuery,
        totalResultsCount,
        requestParams,
        results,
        serpType,
    }: SearchEventParams): SearchEvent {
        const eventDetail: SearchEvent['detail'] = {
            data: {
                searchQuery,
                totalResultsCount,
                requestParams,
                results,
                serpType,
            },
        };
        return this.createEvent<SearchEvent>(SERPEventType.SEARCH, eventDetail);
    }

    public createOpenEvent({ serpType }: OpenEventParams): OpenEvent {
        const eventDetail: OpenEvent['detail'] = {
            data: { serpType },
        };
        return this.createEvent<OpenEvent>(SERPEventType.OPEN, eventDetail);
    }

    public createCloseEvent({
        searchQuery,
        resultsCount,
        loadingFinished,
        serpType,
    }: CloseEventParams): SERPCloseEvent {
        const eventDetail: SERPCloseEvent['detail'] = {
            data: {
                searchQuery,
                resultsCount,
                loadingFinished,
                serpType,
            },
            deliveryConfirmations: [SERPConfirmationEventType.CLOSE_AA_CONFIRM],
        };

        return this.createEvent<SERPCloseEvent>(SERPEventType.CLOSE, eventDetail);
    }

    public createGoToResultEvent({
        searchQuery,
        resultsCount,
        resultType,
        url,
        order,
        serpType,
        button,
        key,
    }: GoToResultEventParams): GoToResultEvent {
        const eventDetail: GoToResultEvent['detail'] = {
            data: {
                searchQuery,
                resultsCount,
                resultType,
                url,
                order,
                serpType,
                button,
                key,
            },
            deliveryConfirmations: [
                SERPConfirmationEventType.GO_TO_RESULT_GA_CONFIRM,
                SERPConfirmationEventType.GO_TO_RESULT_AA_CONFIRM,
            ],
        };
        return this.createEvent<GoToResultEvent>(SERPEventType.GO_TO_RESULT, eventDetail);
    }

    public createIndicesFilterEvent({ searchQuery, actionPayload }: IndicesFilterEventParams): IndicesFilterEvent {
        return this.createEvent<IndicesFilterEvent>(SERPEventType.INDICES_FILTER, {
            data: { searchQuery, actionPayload },
        });
    }

    public createFilterEvent({ searchQuery, actionPayload }: FilterEventParams): FilterEvent {
        return this.createEvent<FilterEvent>(SERPEventType.FILTER, {
            data: { searchQuery, actionPayload },
        });
    }

    public createCancelFilterEvent({ searchQuery, actionPayload }: CancelFilterEventParams): CancelFilterEvent {
        return this.createEvent<CancelFilterEvent>(SERPEventType.CANCEL_FILTER, {
            data: { searchQuery, actionPayload },
        });
    }

    public createCancelAllFiltersEvent({
        searchQuery,
        actionPayload,
    }: CancelAllFiltersEventParams): CancelAllFiltersEvent {
        return this.createEvent<CancelAllFiltersEvent>(SERPEventType.CANCEL_ALL_FILTERS, {
            data: { searchQuery, actionPayload },
        });
    }

    public createErrorEvent({ searchQuery, error }: ErrorEventParams): SERPErrorEvent {
        return this.createEvent<SERPErrorEvent>(SERPEventType.ERROR, { data: { searchQuery, error } });
    }

    public createConfirmationEvent(type: SERPConfirmationEventType): CustomEvent {
        return this.createEvent<CustomEvent>(type, {});
    }

    private createEvent<EventType>(type: string, detail?: SERPEvent['detail']): EventType {
        const eventObj = new CustomEvent(type, { detail, bubbles: false }) as EventType;
        return eventObj;
    }
}
